import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API } from '../services/api';
import { useAuth } from './AuthContext';

const OrganizationContext = createContext({
    primaryColor: '#4f46e5',
    secondaryColor: '#6b7280',
    logoUrl: null,
    subscriptionPlan: 'free',
    loading: true,
    reload: () => {}
});

export function OrganizationProvider({ children }) {
    const { user } = useAuth();
    const [organizationData, setOrganizationData] = useState({
        primaryColor: '#4f46e5',
        secondaryColor: '#6b7280',
        logoUrl: null,
        subscriptionPlan: 'free',
        loading: true
    });

    const loadOrganizationBranding = useCallback(async () => {
        if (!user?.organization_id) {
            setOrganizationData(prev => ({ ...prev, loading: false }));
            return;
        }

        try {
            const response = await API.getOrganizationInfo(user.organization_id);
            if (response?.data?.organization) {
                const org = response.data.organization;
                setOrganizationData({
                    primaryColor: org.primary_color || '#4f46e5',
                    secondaryColor: org.secondary_color || '#6b7280',
                    logoUrl: org.logo_url || null,
                    subscriptionPlan: org.subscription_plan || 'free',
                    loading: false
                });
            }
        } catch (error) {
            console.error('Error loading organization branding:', error);
            setOrganizationData(prev => ({ ...prev, loading: false }));
        }
    }, [user?.organization_id]);

    useEffect(() => {
        loadOrganizationBranding();
    }, [loadOrganizationBranding]);

    const value = {
        ...organizationData,
        reload: loadOrganizationBranding
    };

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
}

export function useOrganization() {
    const context = useContext(OrganizationContext);
    if (context === undefined) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return context;
}

export default OrganizationContext;
